import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ServiceFactory } from '../../services/factory/ServiceFactory';
import {
  Organization,
  NewOrganization,
  OrganizationType,
  ORGANIZATION_TYPE_LABELS
} from '../../types/organization';

interface OrganizationDialogProps {
  organization: Organization | null; // null = create mode, object = edit mode
  onClose: () => void;
  onSave: () => void;
}

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
  organization,
  onClose,
  onSave
}) => {
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState<OrganizationType>('tpa');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiService = ServiceFactory.createApiService();
  const isEditMode = organization !== null;

  // Initialize form with existing data in edit mode
  useEffect(() => {
    if (organization) {
      setName(organization.name);
      // Only set type if it's a user-facing type (not 'admin')
      if (organization.organization_type !== 'admin') {
        setOrgType(organization.organization_type as OrganizationType);
      }
    }
  }, [organization]);

  // Validation
  const validate = (): boolean => {
    if (!name.trim()) {
      setError('Organization name is required');
      return false;
    }

    if (name.trim().length < 2) {
      setError('Organization name must be at least 2 characters');
      return false;
    }

    if (name.trim().length > 255) {
      setError('Organization name must be less than 255 characters');
      return false;
    }

    if (!orgType) {
      setError('Organization type is required');
      return false;
    }

    setError(null);
    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        // Update existing organization
        await apiService.updateOrganization(organization.id, {
          name: name.trim(),
          organization_type: orgType
        });
      } else {
        // Create new organization
        const newOrg: NewOrganization = {
          name: name.trim(),
          organization_type: orgType
        };
        await apiService.createOrganization(newOrg);
      }

      onSave();
    } catch (err: any) {
      console.error('Error saving organization:', err);
      setError(err.message || 'Failed to save organization');
    } finally {
      setLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4 bg-night-900/50">
      <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-night-700">
          <h3 className="text-lg font-semibold text-night dark:text-white">
            {isEditMode ? 'Edit Organization' : 'Create Organization'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Name field */}
            <div>
              <label className="block text-sm font-medium text-night dark:text-white mb-2">
                Organization Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                placeholder="Enter organization name"
                autoFocus
              />
            </div>

            {/* Type field */}
            <div>
              <label className="block text-sm font-medium text-night dark:text-white mb-2">
                Organization Type
              </label>
              <select
                value={orgType}
                onChange={(e) => setOrgType(e.target.value as OrganizationType)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
              >
                <option value="tpa">{ORGANIZATION_TYPE_LABELS.tpa}</option>
                <option value="broker">{ORGANIZATION_TYPE_LABELS.broker}</option>
                <option value="employer">{ORGANIZATION_TYPE_LABELS.employer}</option>
                {/* Note: 'admin' type is NOT included in dropdown */}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select the type of organization
              </p>
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-night-700">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-night dark:text-white border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && (
              <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
            )}
            {isEditMode ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDialog;
